/**
 * Clear all user-specific cache entries
 * This should be called when users log in/out to prevent data leaks
 * 
 * CRITICAL SECURITY: This prevents users from seeing each other's private research data
 */

import { apiCache, clearInFlightRequests } from './cache'
import { clearProfileCache } from './profileCache'

// Track the last cleared user ID to detect user switches
let lastClearedUserId: number | null = null

/**
 * Aggressively clear ALL user-related cache to prevent data leakage between users.
 * Should be called BEFORE setting new user data on login and AFTER clearing user on logout.
 */
export function clearUserCache() {
  console.log('[Security] Clearing all user cache to prevent data leakage')
  
  // Clear ALL in-memory cache and in-flight requests first
  // In-flight requests MUST be cleared to prevent a previous user's
  // pending API response from being cached under the new user's session
  apiCache.clear()
  clearInFlightRequests()
  clearProfileCache()
  
  // Clear the global user reference to prevent stale data
  // This will be set again by AuthContext after clearing
  
  // Aggressively clear localStorage - remove ALL potentially user-related data
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (
        // Cache keys
        key.startsWith('cache_') ||
        key.includes('user_research') ||
        key.includes('saved_research') ||
        key.includes('research_my') ||
        key.includes('public_research') ||
        key.includes('research_detail') ||
        // Profile data
        key.includes('profile') ||
        key.includes('avatar') ||
        // Any other user-specific data
        key.includes('insightatlas-') && !key.includes('theme')
      )) {
        keysToRemove.push(key)
      }
    }
    
    // Remove all identified keys
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch (e) {
        console.warn(`Failed to remove ${key}:`, e)
      }
    })
    
    console.log(`[Security] Cleared ${keysToRemove.length} cache entries from localStorage`)
  } catch (e) {
    console.warn('Failed to clear localStorage user data:', e)
  }
  
  // Reset tracking
  lastClearedUserId = null
}

/**
 * Clear cache and set a flag to track which user the cache was cleared for.
 * This helps detect if we need to re-clear when user switches.
 */
export function clearCacheForUser(userId: number | null) {
  if (userId !== lastClearedUserId) {
    console.log(`[Security] User changed from ${lastClearedUserId} to ${userId}, clearing cache`)
    clearUserCache()
    lastClearedUserId = userId
  }
}

/**
 * Check if cache needs to be cleared for a different user
 */
export function shouldClearCacheForUser(userId: number | null): boolean {
  return userId !== lastClearedUserId
}

export function clearAllCache() {
  apiCache.clear()
  clearInFlightRequests()
  clearProfileCache()
  
  // Also clear localStorage cache entries
  try {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key)
      }
    })
  } catch (e) {
    console.warn('Failed to clear localStorage cache:', e)
  }
  
  console.log('[Security] Cleared all API cache')
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  const win = window as any
  win.clearUserCache = clearUserCache
  win.clearAllCache = clearAllCache
}
